/**
 * Session management utility for storing and retrieving user session data
 */

export interface UserSession {
  token: string;
  userName: string;
  mobile: string;
  email: string;
}

const SESSION_STORAGE_KEY = 'saaed_user_session';
const USER_DATA_STORAGE_KEY = 'saaed_user_data'; // Keep for backward compatibility

/**
 * Saves user session data to sessionStorage
 * This includes the authentication token which will be used for all API calls
 */
export function saveSession(session: UserSession): void {
  try {
    // Save complete session data including token
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    
    // Also save user data for backward compatibility
    const userData = {
      username: session.userName,
      mobileNumber: session.mobile,
    };
    sessionStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(userData));
    
    // Verify token was saved
    if (!session.token) {
      console.warn('Warning: Session saved but token is empty');
    } else {
      console.log('✅ Session saved successfully with token');
    }
  } catch (error) {
    console.error('Failed to save session:', error);
    throw error;
  }
}

/**
 * Retrieves user session data from sessionStorage
 */
export function getSession(): UserSession | null {
  try {
    const sessionData = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionData) {
      return null;
    }
    return JSON.parse(sessionData) as UserSession;
  } catch (error) {
    console.error('Failed to retrieve session:', error);
    return null;
  }
}

/**
 * Checks if user is authenticated
 */
export function isAuthenticated(): boolean {
  const session = getSession();
  return session !== null && !!session.token;
}

/**
 * Clears user session data
 */
export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(USER_DATA_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear session:', error);
  }
}

/**
 * Gets the authentication token from session storage
 * This token should be included in Authorization header for all authenticated API calls
 * @returns The JWT token string or null if not authenticated
 */
export function getToken(): string | null {
  const session = getSession();
  if (session?.token) {
    return session.token;
  }
  return null;
}

/**
 * Gets the full session including token
 * @returns Complete session object with token, username, mobile, and email
 */
export function getFullSession(): UserSession | null {
  return getSession();
}

/**
 * Gets user data in the format expected by the app
 */
export function getUserData(): { username: string; mobileNumber: string } | null {
  const session = getSession();
  if (!session) {
    return null;
  }
  return {
    username: session.userName,
    mobileNumber: session.mobile,
  };
}

